import { Box } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import {
  useDeleteSchedulerSOR,
  useScheduleSOR,
  useUpdateSchedulerSOR,
  useUploadScheduleDataFile,
} from './SOR.data'
import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { SORTable } from './SOR.table'
import { TAB_DIRECTORIES_MAPPING, SOR_DEFAULT_NEW_ROW } from './SORTableColumns'
import { SOR_DATA_CONFIGS } from './SORDataConfig'
import { PillButton } from '../SchedulerDesign'

interface SORViewProps<T> {
  API: string
}

export const SORView = <T extends Record<string, unknown>>({
  API,
}: SORViewProps<T>) => {
  const directory =
    TAB_DIRECTORIES_MAPPING[API as keyof typeof TAB_DIRECTORIES_MAPPING]
  const { data: responseData = { data: [], predicate: undefined }, isLoading } =
    useScheduleSOR(directory)
  const { data: serverData = [], predicate } = responseData
  const dataConfig = SOR_DATA_CONFIGS[API as keyof typeof SOR_DATA_CONFIGS]

  // Locally-added rows pending save. Negative ids avoid collision with the
  // positive ids the server assigns.
  const [localNewRows, setLocalNewRows] = useState<T[]>([])
  const [nextNewRowId, setNextNewRowId] = useState(-1)
  const data = useMemo(
    () => [...serverData, ...localNewRows],
    [serverData, localNewRows]
  )
  const newRowFactory =
    SOR_DEFAULT_NEW_ROW[API as keyof typeof SOR_DEFAULT_NEW_ROW]
  const handleAddRow = newRowFactory
    ? () => {
        const newRow = newRowFactory(nextNewRowId) as T
        setLocalNewRows([...localNewRows, newRow])
        setNextNewRowId(nextNewRowId - 1)
      }
    : undefined

  const onUpdateSuccess = () => {
    setLocalNewRows([])
    toast.success('Scheduler SOR updated successfully')
  }
  const onUpdateFailure = () => {
    toast.error('Failed to update Scheduler SOR')
  }
  const { mutate: saveData, isLoading: isUpdating } = useUpdateSchedulerSOR(
    onUpdateSuccess,
    onUpdateFailure
  )

  const [allChangedRows, setAllChangedRows] = useState<Record<number, T>>({})

  const updateTableData = (changedRows: Record<number, T>) => {
    setAllChangedRows({ ...allChangedRows, ...changedRows })
  }

  useEffect(() => {
    if (Object.keys(allChangedRows).length > 0) {
      saveData({ data: allChangedRows, API: directory })
      setAllChangedRows({})
    }
  }, [allChangedRows])

  const [updateRowsStatus, setUpdateRowsStatus] = useState({})

  const onUploadSuccess = (mergeInfo: Record<string, unknown>) => {
    setUpdateRowsStatus(mergeInfo)
    toast.success('Data uploaded successfully')
  }
  const onUploadFailure = () => {
    toast.error('Failed to upload data')
  }
  const { mutate: uploadScheduleData, isLoading: isUploading } =
    useUploadScheduleDataFile(onUploadSuccess, onUploadFailure)

  const uploadFile = (file: File) => {
    uploadScheduleData({ file, API: directory })
  }

  const onDeleteSuccess = () => {
    toast.success('Rows deleted successfully')
  }
  const onDeleteFailure = () => {
    toast.error('Failed to delete rows')
  }
  const { mutateAsync: deleteData, isLoading: isDeleting } =
    useDeleteSchedulerSOR(onDeleteSuccess, onDeleteFailure)

  const deleteTableData = (rows: T[]) => deleteData({ rows, API: directory })

  return (
    <>
      {handleAddRow && (
        <Box sx={{ mb: 1 }}>
          <PillButton kind="primary" icon={<AddIcon />} onClick={handleAddRow}>
            Add row
          </PillButton>
        </Box>
      )}
      <SORTable<T>
        data={data}
        isLoading={isLoading}
        updateTableData={updateTableData}
        uploadScheduleData={uploadFile}
        isUploading={isUploading}
        isSaving={isUpdating}
        updateRowsStatus={updateRowsStatus}
        predicate={predicate}
        dataConfig={dataConfig}
        API={API}
        onDeleteRows={deleteTableData}
        isDeleting={isDeleting}
      />
    </>
  )
}
